import { Inject, Injectable, Optional } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import * as _moment from 'moment-hijri';
import { Moment } from 'moment-hijri';

const moment = _moment;

@Injectable()
export class HijriMomentDateAdapter extends DateAdapter<Moment> {
  constructor(@Optional() @Inject(MAT_DATE_LOCALE) private _locale: string) {
    super();
    this.setLocale(_locale || 'ar-SA');
  }

  setLocale(locale: string) {
    super.setLocale(locale);
    moment.locale(locale);
  }

  getYear(date: Moment): number {
    return date.iYear();
  }

  getMonth(date: Moment): number {
    return date.iMonth();
  }

  getDate(date: Moment): number {
    return date.iDate();
  }

  getDayOfWeek(date: Moment): number {
    return date.day();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    // Hijri month names in Arabic
    return [
      'محرم', 'صفر', 'ربيع الأول', 'ربيع الآخر', 'جمادى الأولى', 'جمادى الآخرة',
      'رجب', 'شعبان', 'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
    ];
  }

  getDateNames(): string[] {
    // Return 1-31 to match Angular Material's expectations
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    switch (style) {
      case 'long':
        return ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      case 'short':
        return ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];
      case 'narrow':
        return ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'];
      default:
        return ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    }
  }

  getYearName(date: Moment): string {
    return String(date.iYear());
  }

  getFirstDayOfWeek(): number {
    return 6; // Saturday for Arabic calendar
  }

  getNumDaysInMonth(date: Moment): number {
    if (!this.isValid(date)) {
      return 30; // Default fallback
    }
    const daysInMonth = date.iDaysInMonth();
    return daysInMonth || 30; // Fallback to 30 if undefined
  }

  clone(date: Moment): Moment {
    return date.clone().locale(this.locale);
  }

  createDate(year: number, month: number, date: number): Moment {
    if (month < 0 || month > 11) {
      throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
    }
    if (date < 1) {
      throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
    }

    const lastDayOfMonth = moment().iYear(year).iMonth(month).endOf('iMonth').iDate();
    if (date > lastDayOfMonth) {
      date = lastDayOfMonth; // Adjust to the last valid day
    }
    // Create a new moment instance with Hijri date
    const result = moment().iYear(year).iMonth(month).iDate(date);

    // Validate the created date
    if (!result.isValid()) {
      throw Error(`Invalid date combination: year=${year}, month=${month}, date=${date}`);
    }

    // Check that the result had the right year, month, and date
    if (result.iYear() !== year || result.iMonth() !== month || result.iDate() !== date) {
      // If the date doesn't match, it means the date was invalid for that month
      // Try to get the last valid day of the month
      const lastDayOfMonth = moment().iYear(year).iMonth(month).endOf('iMonth').iDate();
      if (date > lastDayOfMonth) {
        throw Error(`Invalid date "${date}" for month with index "${month}". Maximum date is ${lastDayOfMonth}.`);
      }
    }

    return result.locale(this.locale);
  }

  today(): Moment {
    return moment().locale(this.locale);
  }

  parse(value: any, parseFormat: string | string[]): Moment | null {
    if (value && typeof value === 'string') {
      const parsed = moment(value, parseFormat, this.locale, true);
      return parsed.isValid() ? parsed : null;
    }
    return value ? moment(value).locale(this.locale) : null;
  }

  format(date: Moment, displayFormat: string): string {
    if (!this.isValid(date)) {
      throw Error('MomentDateAdapter: Cannot format invalid date.');
    }
    return date.locale(this.locale).format(displayFormat);
  }

  addCalendarYears(date: Moment, years: number): Moment {
    return date.clone().add(years, 'iYear');
  }

  addCalendarMonths(date: Moment, months: number): Moment {
    return date.clone().add(months, 'iMonth');
  }

  addCalendarDays(date: Moment, days: number): Moment {
    return date.clone().add(days, 'day');
  }

  toIso8601(date: Moment): string {
    return date.toISOString();
  }

  deserialize(value: any): Moment | null {
    let date;
    if (value instanceof Date) {
      date = moment(value);
    } else if (this.isDateInstance(value)) {
      // Note: assumes that cloning also sets the correct locale.
      return this.clone(value);
    } else if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      date = moment(value, moment.ISO_8601).locale(this.locale);
    } else if (typeof value === 'number') {
      date = moment(value).locale(this.locale);
    }
    if (date && this.isValid(date)) {
      return date;
    }
    return super.deserialize(value);
  }

  isDateInstance(obj: any): boolean {
    return moment.isMoment(obj);
  }

  isValid(date: Moment): boolean {
    return date && date.isValid();
  }

  invalid(): Moment {
    return moment.invalid();
  }

  getCalendarName(): string {
    return 'hijri';
  }
}
